/**
 * helpRegistry — per-module help text for HelpTip.
 *
 * Keys follow the convention:  "{entityName}.{fieldOrAction}"
 * e.g. "students.name", "students.status", "students.edit"
 *
 * To add a new entity: append entries with the entity prefix.
 */

import type { HelpEntry } from './types';

export const helpRegistry: Record<string, HelpEntry> = {
  // ── Students ──────────────────────────────────────────────────────────
  'students.name': {
    label: 'Full name of the student as it appears in records.',
    example: 'e.g. Aditya Sharma',
  },
  'students.email': {
    label: 'Contact email for the student. Used for invoices and notifications.',
    example: 'e.g. aditya@example.com',
  },
  'students.phone': {
    label: 'Primary contact phone number in international format.',
    example: 'e.g. +91 98765 43210',
  },
  'students.status': {
    label:
      'Enrollment status. Active = attending classes; Inactive = paused; Archived = soft-deleted.',
  },
  'students.tags': {
    label: 'Free-form labels to group or filter students.',
    example: 'e.g. "trial", "scholarship", "online"',
  },
  'students.enrolled': {
    label: 'Date the student record was first created in the system.',
  },
  'students.branch': {
    label: 'Branch this student is primarily associated with.',
  },
  'students.dob': {
    label: 'Date of birth — used for age calculations and birthday reminders.',
    example: 'e.g. 2010-06-15',
  },
  'students.gender': {
    label: 'Gender identity of the student.',
  },
  'students.notes': {
    label: 'Internal notes visible only to staff. Not shared with students or guardians.',
  },
  'students.photo': {
    label: 'Profile picture shown in lists and the detail header.',
  },
  'students.guardians': {
    label: 'Parents or guardians associated with this student.',
  },
  'students.edit': {
    label: 'Fetch the latest record and open the edit form.',
  },
  'students.archive': {
    label: 'Soft-delete the student. The record is preserved but hidden from active lists.',
  },
  'students.search': {
    label: 'Prefix-match search on student name. E.g. "ada" matches "Aditya" but not "Radhakrishna".',
  },
  'students.filter.status': {
    label: 'Filter the list by enrollment status.',
  },
  'students.payment_status': {
    label: 'Payment status derived from the most recent invoice.',
    example: '"✓ Paid" = latest invoice paid. "Outstanding" = unpaid/partial.',
  },
  'students.remaining_classes': {
    label: 'Total remaining sessions across all active class packages.',
    example: 'e.g. 8 = 3 classes left in Guitar + 5 in Keyboard packages.',
  },

  // ── Staff (expanded in 11E.4) ─────────────────────────────────────────
  'staff.name': { label: 'Full name of the staff member.' },
  'staff.role': { label: 'RBAC role. Determines which ERP sections the staff member can access.' },
  'staff.status': { label: 'Active = can log in; Inactive = login disabled.' },
  'staff.staff_type': {
    label: 'Free-form staff classification, e.g. instructor / admin_staff / part_time.',
    example: 'Full staff_type dropdown ships in 11E.5.',
  },
  'staff.filter.status': { label: 'Filter the list by login status.' },
  'staff.filter.role': { label: 'Filter the list by RBAC role.' },
  'staff.attendance_leaves': {
    label: 'Staff attendance and leave tracking. Read-only stub until 11E.6.',
  },
  'staff.salary_pay': {
    label: 'Salary and pay details. Visible to admin/owner roles only.',
  },

  // ── Subjects (expanded in 11E.4) ──────────────────────────────────────
  'subjects.name': { label: 'Name of the subject or discipline.' },
  'subjects.category': { label: 'Grouping used for filtering and package name derivation.' },
  'subjects.duration': { label: 'Default session length in minutes.' },
  'subjects.price': { label: 'Price charged per single session, in ₹.' },
  'subjects.filter.status': { label: 'Filter the list by active/inactive status.' },
  'subjects.filter.category': { label: 'Filter the list by category.' },

  // ── Packages (expanded in 11E.4) ──────────────────────────────────────
  'packages.name': { label: 'Name of the class package.' },
  'packages.sessions': { label: 'Number of sessions included in the package.' },
  'packages.name_preview': {
    label: 'Auto-derived from Discipline | Audience | Sessions/Cycle. Override wins if set.',
    example: 'e.g. "Violin | Beginner – Grade 2 | Yearly"',
  },
  'packages.type': { label: 'One-time = single purchase. Subscription = recurring billing cycle.' },
  'packages.billing_cycle': { label: 'How often a subscription package re-bills.' },
  'packages.pro_rata': {
    label: 'When on, a mid-cycle subscription start is charged only for remaining days.',
  },
  'packages.published_online': {
    label: 'When on, this package is listed (read-only) on the public booking page.',
  },
  'packages.audience': {
    label: 'Grade/level segment shown in the derived name, e.g. "Grade 1-2" or "All grades".',
  },
  'packages.registration_fee': { label: 'One-time fee charged in addition to the package price.' },
  'packages.filter.status': { label: 'Filter the list by active/inactive (archived) status.' },
  'packages.filter.subject': { label: 'Filter the list by included subject.' },

  // ── Invoices (11F.7) ──────────────────────────────────────────────────
  'invoices.number': {
    label:
      'Sequential invoice number, allocated once when the invoice is issued. Drafts have none.',
    example: 'e.g. RB-2026-0042',
  },
  'invoices.invoicee': {
    label: 'The student this invoice bills. Search matches this name as well as the number.',
  },
  'invoices.issue_date': { label: 'Date the invoice was raised.' },
  'invoices.due_date': { label: 'Date payment is expected by. Overdue invoices are past this.' },
  'invoices.total': { label: 'Invoice total including tax.' },
  'invoices.amount_due': {
    label: 'Outstanding balance — total minus payments received. Zero once fully paid.',
  },
  'invoices.items': {
    label: 'Line items, shown by subject name where the line references one.',
  },
  'invoices.status': {
    label:
      'Draft = editable, not yet issued. Issued/Partially paid = owing. Paid = settled. Void = cancelled with a reason (never deleted).',
  },
  'invoices.filter.status': { label: 'Filter the list by invoice status.' },
  'invoices.summary.invoiced': {
    label: 'Total value of all issued invoices in range. Drafts and voided invoices are excluded.',
  },
  'invoices.summary.collected': {
    label: 'Payments received against those invoices.',
  },
  'invoices.summary.due': {
    label: 'Outstanding balance still owed across invoices that are not fully paid.',
  },

  // ── Payments (11F.7) ──────────────────────────────────────────────────
  'payments.date': { label: 'Date the payment was received.' },
  'payments.invoice_number': {
    label: 'The invoice this payment was applied to. Click the row to open it.',
  },
  'payments.student': { label: 'Student the payment was received for.' },
  'payments.amount': { label: 'Amount received, in the tenant currency.' },
  'payments.refunded': {
    label:
      'Total refunded against this payment. A refund returns money but leaves the payment valid — unlike voiding, which marks it as recorded in error.',
  },
  'payments.method': { label: 'How the payment was made — UPI, cash, bank transfer, etc.' },
  'payments.reference': {
    label: 'Transaction or receipt reference captured at the time of payment.',
  },
  'payments.status': { label: 'Active = counted. Voided = reversed as a data correction.' },
  'payments.filter.status': { label: 'Filter the list by payment status.' },
};



